/**
 * ChannelFilter — persistent pill selector for filtering marketing views by channel.
 *
 * Shows "All Channels", "Meta", "Google" as selectable pills.
 * Only connected channels are enabled; others are greyed out.
 */

import type { MarketingChannel } from '@/types/marketing'

interface ChannelFilterProps {
  value: MarketingChannel | null  // null = all channels
  onChange: (channel: MarketingChannel | null) => void
  connectedChannels: MarketingChannel[]
}

const CHANNELS: { key: MarketingChannel | null; label: string; icon: string }[] = [
  { key: null, label: 'All Channels', icon: '' },
  { key: 'meta', label: 'Meta', icon: '🟣' },
  { key: 'google', label: 'Google', icon: '🔵' },
]

export function ChannelFilter({ value, onChange, connectedChannels }: ChannelFilterProps) {
  return (
    <div className="flex items-center gap-1">
      {CHANNELS.map(({ key, label, icon }) => {
        const isSelected = value === key
        const isDisabled = key !== null && !connectedChannels.includes(key)

        return (
          <button
            key={key ?? 'all'}
            onClick={() => !isDisabled && onChange(key)}
            disabled={isDisabled}
            className={`
              inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium
              transition-colors duration-150
              ${isSelected
                ? 'bg-primary text-primary-foreground'
                : isDisabled
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-muted text-foreground hover:bg-muted-foreground/10'
              }
            `}
          >
            {icon && <span className="leading-none">{icon}</span>}
            {label}
          </button>
        )
      })}
    </div>
  )
}

export default ChannelFilter
